import React from 'react';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Skeleton } from '@client/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';
import { AdvisorUsageTable } from '@/components/usage-analytics/AdvisorUsageTable';
import { USAGE_METRICS_CONFIG } from '@shared/config/usageMetricsConfig';
import { useAdvisorUsageAnalytics } from '@/hooks/useAdvisorUsageAnalytics';
import { formatMetricValue, buildMetricTooltip } from '@/utils/metricFormatting';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';

function ErrorState({ error }: { error: string }) {
  return (
    <Alert
      variant="destructive"
      className="mt-4"
    >
      <AlertDescription>
        <div className="space-y-4">
          <p className="font-semibold">Error Loading Usage Data</p>
          <p>{error}</p>
        </div>
      </AlertDescription>
    </Alert>
  );
}

export function AdvisorUsageAnalytics() {
  const { usageAnalytics, calculatedMetrics, advisorsList, activeAdvisorsCount, refetch } =
    useAdvisorUsageAnalytics();

  const isLoading = usageAnalytics.isLoading;

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader title="Usage Dashboard" />

      {/* Error State */}
      {!isLoading && usageAnalytics.errorMessage && (
        <ErrorState error={usageAnalytics.errorMessage} />
      )}

      {/* Usage Dashboard UI */}
      {!usageAnalytics.errorMessage && (
        <UsageAnalyticsView
          activeAdvisorsCount={activeAdvisorsCount}
          isLoading={isLoading}
          calculatedMetrics={calculatedMetrics}
          advisorsList={advisorsList}
          refetch={refetch}
        />
      )}
    </main>
  );
}

interface UsageAnalyticsViewProps {
  activeAdvisorsCount: number;
  isLoading: boolean;
  calculatedMetrics: UsageMetrics;
  advisorsList: AdvisorUsageData[];
  refetch?: () => void;
}

export function UsageAnalyticsView({
  activeAdvisorsCount,
  isLoading,
  calculatedMetrics,
  advisorsList,
  refetch,
}: UsageAnalyticsViewProps) {
  return (
    <>
      {/* Top Metrics Bar */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <TooltipProvider>
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4 lg:grid-cols-7">
            <MetricCard
              label="Active Advisors"
              value={activeAdvisorsCount}
              description="Number of advisors who have logged in at least once"
              isLoading={isLoading}
            />
            {USAGE_METRICS_CONFIG.slice(0, 6).map(metricDef => {
              const rawValue = calculatedMetrics[metricDef.key];
              const { formattedValue, fullValue } = formatMetricValue(rawValue, metricDef.format);

              return (
                <MetricCard
                  key={metricDef.key}
                  label={metricDef.label}
                  value={formattedValue}
                  description={metricDef.description}
                  fullValue={fullValue}
                  isLoading={isLoading}
                />
              );
            })}
          </div>
        </TooltipProvider>
      </div>

      {/* Advisors Table */}
      <AdvisorUsageTable
        advisors={advisorsList}
        onRefresh={refetch}
        isRefreshing={isLoading}
      />
    </>
  );
}

function MetricCard({
  label,
  value,
  description,
  fullValue,
  isLoading,
}: {
  label: string;
  value: string | number;
  description?: string;
  fullValue?: string | null;
  isLoading?: boolean;
}) {
  const tooltipText = buildMetricTooltip(fullValue, description);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex h-full cursor-help flex-col">
          <p className="mb-1 h-10 text-sm leading-tight text-gray-700">{label}</p>
          {isLoading ? (
            <Skeleton className="h-8 w-16 rounded" />
          ) : (
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          )}
        </div>
      </TooltipTrigger>
      {tooltipText && (
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
